import { execFile, execFileSync } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile, writeFile, mkdir, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'

const execFileAsync = promisify(execFile)

/** Launcher for Kilo Code — open-source fork of OpenCode (CLI-compatible) */
export class KiloCodeLauncher implements ProviderLauncher {
  readonly name = 'kilo-code'
  readonly displayName = 'Kilo Code'
  readonly pricingHint = '🆓 Gratuit (open-source, pay-as-you-go tokens)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('kilo', ['--version'])
      return true
    } catch {
      try {
        await execFileAsync('kilocode', ['--version'])
        return true
      } catch {
        return false
      }
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('kilo', ['--version'])
      return stdout.trim()
    } catch {
      try {
        const { stdout } = await execFileAsync('kilocode', ['--version'])
        return stdout.trim()
      } catch {
        return null
      }
    }
  }

  private getBinary(): string {
    try {
      execFileSync('kilo', ['--version'], { stdio: 'ignore' })
      return 'kilo'
    } catch {
      try {
        execFileSync('kilocode', ['--version'], { stdio: 'ignore' })
        return 'kilocode'
      } catch {
        return 'npx'
      }
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt = config.systemPromptContent ?? await readFile(config.systemPromptPath, 'utf-8')

    // Kilo reads agent configs from ~/.config/kilo/agent/<name>.md.
    // Write the system prompt there so the agent is "loaded" but
    // the candidate cannot see it (they don't have access to the home dir).
    const agentDir = join(process.env['HOME'] || '/tmp', '.config', 'kilo', 'agent')
    await mkdir(agentDir, { recursive: true })

    const agentConfig = `---
name: tonty-interview
description: Technical interview evaluation
mode: primary
permissions:
  - bash
  - read
  - edit
  - glob
  - grep
  - webfetch
  - websearch
---

${systemPrompt}
`
    const agentPath = join(agentDir, 'tonty-interview.md')
    await writeFile(agentPath, agentConfig)

    const args: string[] = []

    args.push(workdir)
    args.push('--agent', 'tonty-interview')

    if (config.model) {
      args.push('--model', config.model)
    }

    const recorder = new SessionRecorder()

    const binary = await resolveBinaryOrFallback(this.getBinary())
    const spawnArgs = this.getBinary() === 'npx' ? ['kilo', ...args] : args

    const ptyProc = getPty().spawn(binary, spawnArgs, {
      name: 'xterm-256color',
      cwd: workdir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(async ({ exitCode, signal }) => {
            cleanup()
            await rm(agentPath, { force: true }).catch(() => {})
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
        await rm(agentPath, { force: true }).catch(() => {})
      },
      recorder,
    }
  }
}
